import { useState, useRef, useEffect, useCallback, useId } from 'react'
import { Headphones, Mic, Play, Pause, X, Loader2, AlertCircle } from 'lucide-react'
import { useApiQuery } from '../../hooks/useApi.js'
import { useT } from '../../i18n/index.js'
import { VoiceRecorderFab } from './VoiceRecorderFab.js'

// ── Types ──

type PlayerState = 'idle' | 'loading' | 'playing' | 'paused' | 'error'

export interface FicheVoiceSectionProps {
  mandatId: string
  mandatLabel?: string  // ex: "Rue des Lilas 12, Uccle" — affiché dans le player TTS
  bienId?: string       // pour les notes vocales liées à un bien spécifique
  autoPlay?: boolean    // déclenche TTS automatiquement après 2s (depuis agenda)
  startSTT?: boolean    // ouvre le recorder immédiatement (depuis AssistantFab)
  voice?: string        // voix TTS, default 'onyx'
  onClose?: () => void  // appelé quand player TTS et recorder se ferment
}

interface VoiceNote {
  _id: string
  audioDurationSec: number
  status: string
  actionsExecuted: { type: string }[]
  transcriptPurgedAt: string | null
  createdAt: string
}

interface VoiceNotesResponse {
  data: VoiceNote[]
  total: number
}

const SPEEDS = [1, 1.5, 2] as const

// ── Helpers ──

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  if (m > 0) return `${m}min${s > 0 ? ` ${s}s` : ''}`
  return `${s}s`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })
}

// ── Status indicator ──

const STATUS_LABELS: Record<string, string> = {
  executed: 'Exécuté',
  undone: 'Annulé',
  error: 'Erreur',
  transcribing: 'Transcription',
  extracting: 'Extraction',
  recording: 'Enregistrement',
}

function StatusDot({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    executed: 'var(--color-status-success)',
    undone: 'var(--color-text-disabled)',
    error: 'var(--color-status-error)',
    transcribing: 'var(--color-status-info)',
    extracting: 'var(--color-status-info)',
    recording: 'var(--color-status-warning)',
  }

  return (
    <div
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ background: colorMap[status] || 'var(--color-text-disabled)' }}
      role="img"
      aria-label={`Statut : ${STATUS_LABELS[status] ?? status}`}
    />
  )
}

// ── Composant principal ──

export function FicheVoiceSection({
  mandatId,
  mandatLabel,
  bienId,
  autoPlay = false,
  startSTT = false,
  voice = 'onyx',
  onClose,
}: FicheVoiceSectionProps) {
  const t = useT()

  // ── TTS state ──
  const [playerState, setPlayerState] = useState<PlayerState>('idle')
  const [playerActive, setPlayerActive] = useState(false)
  const [speedIdx, setSpeedIdx] = useState(0)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const autoPlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const listenBtnRef = useRef<HTMLButtonElement | null>(null) // focus return on player close
  const progressId = useId() // stable id for progressbar aria

  // ── STT state ──
  const [showRecorder, setShowRecorder] = useState(startSTT)

  // ── Voice notes history ──
  const { data, isLoading: notesLoading, refetch } = useApiQuery<VoiceNotesResponse>(
    `/api/voice-notes?bienId=${bienId ?? ''}&limit=5`,
    { enabled: !!bienId },
  )

  const notes = data?.data || []

  // ── Audio src ──
  // LL-065 : la route /api/mandats/:id/audio-fiche streame directement l'audio (Content-Type: audio/mpeg)
  const audioSrc = `/api/mandats/${mandatId}/audio-fiche?voice=${voice}`

  // ── TTS: start playback ──
  const startPlayback = useCallback(() => {
    setPlayerState('loading')
    setErrorMsg(null)
    setPlayerActive(true)
  }, [])

  // ── TTS: auto-play depuis agenda (2s de délai) ──
  useEffect(() => {
    if (autoPlay && playerState === 'idle') {
      autoPlayTimerRef.current = setTimeout(() => {
        startPlayback()
      }, 2000)
    }
    return () => {
      if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current)
    }
  }, [autoPlay, playerState, startPlayback])

  // ── TTS: appliquer la vitesse quand elle change ──
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[speedIdx]
    }
  }, [speedIdx])

  // ── TTS: cleanup à la destruction ──
  useEffect(() => {
    return () => {
      if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
    }
  }, [])

  function handleTogglePlay() {
    if (playerState === 'idle' || playerState === 'error') {
      startPlayback()
      return
    }
    if (!audioRef.current) return
    if (playerState === 'playing') {
      audioRef.current.pause()
      setPlayerState('paused')
    } else if (playerState === 'paused') {
      audioRef.current.play()
      setPlayerState('playing')
    }
  }

  function handleClose() {
    if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current)
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
    }
    setPlayerState('idle')
    setPlayerActive(false)
    setCurrentTime(0)
    setDuration(0)
    setErrorMsg(null)
    onClose?.()
    // WCAG 2.4.3 — focus retourne au bouton déclencheur
    requestAnimationFrame(() => listenBtnRef.current?.focus())
  }

  function handleSpeedToggle() {
    setSpeedIdx((i) => (i + 1) % SPEEDS.length)
  }

  // ── STT: recorder complete ──
  const handleRecordComplete = useCallback(() => {
    setShowRecorder(false)
    refetch()
    onClose?.()
  }, [refetch, onClose])

  const isPlayerActive = playerState !== 'idle' && playerState !== 'error'

  return (
    <div className="flex flex-col gap-4">
      {/* ── Section title ── */}
      <h3
        className="text-xs font-medium uppercase tracking-wider"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        Notes vocales
      </h3>

      {/* ── Deux boutons côte à côte ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Bouton Écouter */}
        <button
          ref={listenBtnRef}
          onClick={handleTogglePlay}
          aria-pressed={isPlayerActive}
          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
          style={{
            color: isPlayerActive ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            borderColor: isPlayerActive ? 'var(--color-primary)' : undefined,
          }}
          aria-label={isPlayerActive ? 'Lecteur en cours' : 'Écouter la fiche audio du bien'}
        >
          {playerState === 'loading' ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Headphones size={16} aria-hidden="true" />
          )}
          {playerState === 'playing'
            ? 'En cours d\'écoute'
            : playerState === 'paused'
              ? 'Reprendre la fiche'
              : playerState === 'loading'
                ? 'Chargement…'
                : 'Écouter la fiche'}
        </button>

        {/* Bouton Dicter — masqué si recorder ouvert */}
        {!showRecorder && (
          <button
            onClick={() => setShowRecorder(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm font-medium transition-[background-color,border-color,color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            style={{ color: 'var(--color-text-secondary)' }}
            aria-label={t.voice.inline.recordAriaLabel}
          >
            <Mic size={16} aria-hidden="true" />
            Dicter une note
          </button>
        )}
      </div>

      {/* ── Erreur TTS (non bloquante) ── */}
      {playerState === 'error' && (
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg border text-sm"
          style={{
            background: 'var(--color-error-light)',
            borderColor: 'var(--color-error)',
            color: 'var(--color-error)',
          }}
          role="alert"
        >
          <AlertCircle size={16} aria-hidden="true" />
          <span>{errorMsg ?? 'Audio indisponible — réessayez'}</span>
          <button
            onClick={startPlayback}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Réessayer
          </button>
          <button
            onClick={handleClose}
            className="ml-1 flex items-center justify-center rounded transition-colors hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-error)]"
            style={{ minHeight: '44px', minWidth: '44px' }}
            aria-label="Fermer"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Historique des notes vocales ── */}
      {bienId && (
        <div className="flex flex-col gap-2">
          {notesLoading ? (
            <div className="flex items-center gap-2 py-4">
              <div
                className="h-4 w-4 animate-spin rounded-full border-2"
                style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }}
              />
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {t.common.loading}
              </span>
            </div>
          ) : notes.length === 0 ? (
            <div
              className="flex flex-col items-center gap-2 rounded-xl py-6 text-center"
              style={{ background: 'var(--color-surface-raised)' }}
            >
              <Mic size={24} style={{ color: 'var(--color-text-disabled)' }} aria-hidden="true" />
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {t.voice.inline.noNotes}
              </p>
            </div>
          ) : (
            notes.map((note) => (
              <div
                key={note._id}
                className="flex items-center justify-between rounded-lg px-3 py-2"
                style={{ background: 'var(--color-surface-raised)' }}
              >
                <div className="flex items-center gap-2">
                  <StatusDot status={note.status} />
                  <div>
                    <p className="text-xs font-medium" style={{ color: 'var(--color-text)' }}>
                      {t.voice.inline.notePrefix} {formatDate(note.createdAt)}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {note.actionsExecuted?.length || 0} {t.voice.inline.actionsLabel} {' · '}
                      {formatDuration(note.audioDurationSec)}
                      {note.transcriptPurgedAt && ` · ${t.voice.history.purged}`}
                    </p>
                  </div>
                </div>

                {note.transcriptPurgedAt && (
                  <span
                    className="rounded-md px-2 py-0.5 text-xs font-medium"
                    style={{
                      background: 'var(--color-status-success-light)',
                      color: 'var(--color-status-success)',
                    }}
                  >
                    {t.voice.history.purged}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Recorder inline via VoiceRecorderFab ── */}
      {showRecorder && (
        <VoiceRecorderFab bienId={bienId} onComplete={handleRecordComplete} />
      )}

      {/* ── Audio element TTS ── */}
      {/* LL-065 : src = route API qui streame l'audio — pas de presigned URL interne */}
      {playerActive && (
        <audio
          ref={audioRef}
          src={audioSrc}
          onCanPlay={() => {
            if (playerState === 'loading' && audioRef.current) {
              audioRef.current.playbackRate = SPEEDS[speedIdx]
              audioRef.current.play()
              setPlayerState('playing')
            }
          }}
          onTimeUpdate={() => {
            if (audioRef.current) setCurrentTime(audioRef.current.currentTime)
          }}
          onLoadedMetadata={() => {
            if (audioRef.current) setDuration(audioRef.current.duration)
          }}
          onEnded={() => setPlayerState('paused')}
          onError={() => {
            setPlayerState('error')
            setErrorMsg('Erreur de lecture audio')
          }}
          preload="none"
        />
      )}

      {/* ── Player flottant TTS (fixed bottom-16) ── */}
      {isPlayerActive && (
        <div
          role="region"
          aria-label="Lecteur de fiche"
          className="fixed bottom-16 left-0 right-0 mx-4 sm:mx-auto sm:max-w-xl z-50"
        >
          <div className="flex items-center gap-3 h-14 px-4 rounded-xl border shadow-lg"
            style={{
              background: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
            }}
          >
            {/* Play/Pause */}
            <button
              onClick={handleTogglePlay}
              disabled={playerState === 'loading'}
              className="flex items-center justify-center rounded-full transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
              style={{
                minHeight: '36px',
                minWidth: '36px',
                background: 'var(--color-primary)',
                color: 'var(--color-text-inverse)',
              }}
              aria-label={playerState === 'playing' ? 'Pause' : 'Lecture'}
            >
              {playerState === 'loading' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : playerState === 'playing' ? (
                <Pause size={16} />
              ) : (
                <Play size={16} />
              )}
            </button>

            {/* Label + progression */}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text)' }}>
                {mandatLabel ? `Fiche · ${mandatLabel}` : 'Fiche audio'}
              </div>
              {duration > 0 ? (
                <div className="flex items-center gap-1 mt-0.5">
                  <div
                    id={progressId}
                    role="progressbar"
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration)}
                    aria-label="Progression audio"
                    className="flex-1 h-1 rounded-full overflow-hidden"
                    style={{ background: 'var(--color-border)' }}
                  >
                    <div
                      className="h-full rounded-full transition-[width]"
                      style={{
                        width: `${Math.min(100, (currentTime / duration) * 100)}%`,
                        background: 'var(--color-primary)',
                      }}
                    />
                  </div>
                  <span
                    className="text-xs tabular-nums shrink-0"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {formatTime(currentTime)}
                  </span>
                </div>
              ) : (
                <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {playerState === 'loading' ? 'Préparation de l\'audio…' : 'En cours'}
                </div>
              )}
            </div>

            {/* Toggle vitesse — 44px min pour cible mobile terrain */}
            <button
              onClick={handleSpeedToggle}
              className="flex items-center justify-center text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] rounded"
              style={{
                minHeight: '44px',
                minWidth: '44px',
                color: 'var(--color-text-secondary)',
              }}
              aria-label={`Vitesse : ${SPEEDS[speedIdx]}x. Appuyer pour changer`}
            >
              {SPEEDS[speedIdx]}x
            </button>

            {/* Fermeture — 44px min pour cible mobile terrain */}
            <button
              onClick={handleClose}
              className="flex items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
              style={{
                minHeight: '44px',
                minWidth: '44px',
                color: 'var(--color-text-secondary)',
              }}
              aria-label="Fermer le lecteur"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
